import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Grid, TextField, MenuItem, Typography, Box, Button, Stack, Autocomplete } from '@mui/material';

// Helper to prettify field names
const prettify = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

// Helper to format ISO date strings
const formatIfDate = (value) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  return value;
};

const actions = [
  { value: '', label: 'All Actions' },
  { value: 'edit', label: 'Edit' },
  { value: 'delete', label: 'Delete' },
  // Add more actions as needed
];

const entities = [
  { value: '', label: 'All Entities' },
  { value: 'raw_stock', label: 'Raw Stock' },
  { value: 'imported_inventory', label: 'Imported Inventory' },
  { value: 'batch_inventory_process', label: 'Inventory Process' },
  // Add more entities as needed
];

const renderDetailsCell = (params) => {
  const { colorMap = {}, purityMap = {}, stockTypeMap = {}, departmentMap = {}, employeeMap = {} } = params.colDef;
  let details = params.row && params.row.details;
  if (typeof details === 'string') {
    try {
      details = JSON.parse(details);
    } catch (e) {
      return <span>{details}</span>;
    }
  }
  if (!details || typeof details !== 'object') return null;

  return (
    <Stack spacing={0.5}>
      {Object.entries(details)
        .filter(([_, value]) => value !== null && value !== '')
        .map(([key, value]) => {
          let displayValue = value;
          if (key === 'stock_type_id' && stockTypeMap[value]) displayValue = stockTypeMap[value];
          if (key === 'karatage' && purityMap[value]) displayValue = purityMap[value];
          if (key === 'color' && colorMap[value]) displayValue = colorMap[value];
          if (key === 'from_department_id' && departmentMap[value]) displayValue = departmentMap[value];
          if (key === 'to_department_id' && departmentMap[value]) displayValue = departmentMap[value];
          if (key === 'from_worker_id' && employeeMap[value]) displayValue = employeeMap[value];
          if (key === 'purity_id' && purityMap[value]) displayValue = purityMap[value];
          if (key === 'is_dept_production') displayValue = value === 1 || value === '1' ? 'Yes' : 'No';
          if (key.includes('weight') && typeof value === 'number') displayValue = value.toFixed(3);
          if (/date|at/i.test(key) && typeof value === 'string') displayValue = formatIfDate(value);
          return (
            <Box key={key} sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {prettify(key)}:
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {displayValue}
              </Typography>
            </Box>
          );
        })}
    </Stack>
  );
};

const columns = [
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    flex: 1,
    minWidth: 180,
    renderCell: (params) => {
      let ts = params.row && params.row.timestamp;
      if (!ts) return '';
      const d = new Date(ts);
      if (!isNaN(d.getTime())) return d.toLocaleString();
      return ts;
    },
  },
  { field: 'user_id', headerName: 'User', flex: 0.7, minWidth: 100 },
  { field: 'action', headerName: 'Action', flex: 0.7, minWidth: 100 },
  { field: 'entity', headerName: 'Entity', flex: 1, minWidth: 120 },
  { field: 'record_id', headerName: 'Record ID', flex: 0.7, minWidth: 100 },
  {
    field: 'details',
    headerName: 'Details',
    flex: 2,
    minWidth: 200,
    renderCell: (params) => renderDetailsCell({ ...params, colDef: { ...params.colDef, colorMap: params.colDef.colorMap, purityMap: params.colDef.purityMap, stockTypeMap: params.colDef.stockTypeMap, departmentMap: params.colDef.departmentMap, employeeMap: params.colDef.employeeMap } }),
    colorMap: undefined, // will be set in ActivityLogTable
    purityMap: undefined,
    stockTypeMap: undefined,
    departmentMap: undefined,
    employeeMap: undefined,
  },
];

const ActivityLogTable = ({
  logs,
  loading,
  error,
  filters,
  onFilterChange,
  onSearch,
  colorMap,
  purityMap,
  stockTypeMap,
  departmentMap,
  employeeMap,
}) => {
  // Inject master data maps into columns
  const columnsWithMaps = columns.map(col =>
    col.field === 'details'
      ? { ...col, colorMap, purityMap, stockTypeMap, departmentMap, employeeMap }
      : col
  );
  return (
    <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}>
        Activity Log
      </Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="From"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.from}
            onChange={e => onFilterChange('from', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="To"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.to}
            onChange={e => onFilterChange('to', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 180 }}>
          <TextField
            select
            label="Action"
            size="small"
            fullWidth
            sx={{ minWidth: 180 }}
            value={filters.action}
            onChange={e => onFilterChange('action', e.target.value)}
          >
            {actions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 180 }}>
          <TextField
            select
            label="Entity"
            size="small"
            fullWidth
            sx={{ minWidth: 180 }}
            value={filters.entity}
            onChange={e => onFilterChange('entity', e.target.value)}
          >
            {entities.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 240 }}>
          <Autocomplete
            options={Object.entries(employeeMap).map(([id, name]) => ({ id, label: id + ' - ' + name }))}
            getOptionLabel={option => option.label || ''}
            value={
              filters.user_id
                ? Object.entries(employeeMap)
                    .map(([id, name]) => ({ id, label: id + ' - ' + name }))
                    .find(opt => opt.id === filters.user_id) || null
                : null
            }
            onChange={(_, newValue) => onFilterChange('user_id', newValue ? newValue.id : '')}
            renderInput={params => (
              <TextField {...params} label="User" size="small" fullWidth />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            clearOnEscape
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ height: '40px' }}
            onClick={onSearch}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      {error && <Box sx={{ color: 'error.main', mb: 2 }}>{error}</Box>}
      <div style={{ height: 500, width: '100%' }}>
        {console.log('ActivityLogTable logs:', logs)}
        <DataGrid
          rows={
            Array.isArray(logs)
              ? logs
                  .filter(row => row && row.timestamp)
                  .map((row, idx) => ({
                    ...row,
                    id: row.id || idx,
                    details: row.details, // pass as-is, parse in renderCell
                    timestamp: row.timestamp,
                  }))
              : []
          }
          columns={columnsWithMaps}
          loading={loading}
          pageSize={20}
          rowsPerPageOptions={[20, 50, 100]}
          disableSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            borderRadius: 2,
            background: '#fff',
            boxShadow: 1,
            fontSize: 14,
            '& .MuiDataGrid-columnHeaders': {
              background: 'rgba(0,0,0,0.04)',
              fontWeight: 'bold',
            },
          }}
        />
      </div>
    </Paper>
  );
};

export default ActivityLogTable; 